import { useCallback, useMemo, useRef, useState } from 'react';
import { isTraceBoardItemEqual } from '../utils/isTraceBoardItemEqual';
export function useTraceBoard(options) {
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const [items, setItemsState] = useState([]);
    // use key to re-evaluate value when columns are updated
    // and change reference of columns to update from hook deps
    const columnsKey = options.columns
        .map(column => `${column.id}-${column.name}`)
        .join(',');
    const columns = useMemo(() => {
        return Array.from(optionsRef.current.columns);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsKey]);
    const updateLinkedItems = useCallback((columnItemMap) => {
        // check only linked column items based on
        // linkedColumnIds to avoid looping through all items
        for (const column of columns) {
            const items = columnItemMap[column.id] || [];
            // skip if no items
            if (items.length === 0) {
                continue;
            }
            for (const linkedColumnId of column.linkedColumnIds) {
                for (const linked of columnItemMap[linkedColumnId] || []) {
                    for (const item of items) {
                        // skip if the same item or not linked
                        if (!optionsRef.current.isLinked(item, linked)) {
                            continue;
                        }
                        // save to linked items
                        item.isLinked = true;
                        linked.linkedItems.push({ id: item.id, columnId: item.columnId });
                    }
                }
            }
        }
    }, [columns]);
    const getConnectors = useCallback((row) => {
        const connectors = [];
        for (const item of row.items) {
            const sourceIndex = columns.findIndex(column => column.id === item.columnId);
            for (const linked of item.linkedItems || []) {
                const targetIndex = columns.findIndex(column => column.id === linked.columnId);
                const adjacent = sourceIndex > -1 &&
                    targetIndex > -1 &&
                    Math.abs(sourceIndex - targetIndex) === 1;
                connectors.push({
                    adjacent,
                    sourceId: item.id,
                    targetId: linked.id,
                    sourceColumnId: item.columnId,
                    targetColumnId: linked.columnId
                });
            }
        }
        return connectors;
    }, [columns]);
    const createRow = useCallback((boardItems, skipLinkedItems = false) => {
        const row = { items: [] };
        // recursively save items to row
        const saveToRow = (item) => {
            // save item to column if it does not exist
            if (!row.items.some(i => isTraceBoardItemEqual(i, item))) {
                row.items.push(item);
            }
            if (skipLinkedItems) {
                return;
            }
            // save linked items
            for (const linkedItem of item.linkedItems) {
                const item = items.find(item => {
                    return isTraceBoardItemEqual(item, linkedItem);
                });
                item && saveToRow(item);
            }
        };
        const rowItems = Array.isArray(boardItems) ? boardItems : [boardItems];
        for (const item of rowItems) {
            saveToRow(item);
        }
        row.connectors = getConnectors(row);
        return row;
    }, [items, getConnectors]);
    const getRows = useCallback(() => {
        const rows = [];
        // build rows for each column
        for (const column of columns) {
            // get items in column that have linked items but are not linked by other items
            const allItems = items.filter(item => {
                return (!item.isLinked &&
                    item.columnId === column.id &&
                    item.linkedItems.length > 0);
            });
            // create multiple rows per item
            for (const item of allItems) {
                rows.push(createRow(item));
            }
        }
        // orphan row: get items that have no relations to other items
        const orphanItems = items.filter(item => !item.isLinked && item.linkedItems.length === 0);
        const orphanRow = createRow(orphanItems, true);
        return { rows, orphanRow };
    }, [columns, items, createRow]);
    const setItems = useCallback((value, done) => {
        setItemsState(itemsState => {
            const items = typeof value === 'function' ? value(itemsState) : value;
            const columnItemMap = {};
            for (const column of columns) {
                columnItemMap[column.id] = [];
            }
            for (const item of items) {
                // clear values before updating linked items
                item.linkedItems = [];
                delete item.isLinked;
                columnItemMap[item.columnId].push(item);
            }
            updateLinkedItems(columnItemMap);
            done === null || done === void 0 ? void 0 : done(items);
            return items;
        });
    }, [columns, updateLinkedItems]);
    const setup = useCallback((options, done) => {
        const boardColumns = options.columns.filter(boardColumn => {
            return columns.some(column => column.id === boardColumn.id);
        });
        const items = [];
        for (const boardColumn of boardColumns) {
            for (const boardItem of boardColumn.items) {
                const item = optionsRef.current.createItem(boardColumn.id, boardItem);
                items.push(item);
            }
        }
        setItems(items, done);
    }, [columns, setItems]);
    const manager = useMemo(() => {
        return { columns, items, getRows, setItems, setup };
    }, [columns, items, getRows, setItems, setup]);
    return manager;
}
//# sourceMappingURL=useTraceBoard.js.map